import React, { useState, useEffect } from 'react';
import {
    MessageSquare,
    AlertTriangle,
    Clock,
    CheckCircle,
    BarChart3,
    Users,
    Plus,
    Search,
    ChevronRight,
    Loader2,
    Sparkles
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import TicketDetailModal from '../Modals/TicketDetailModal';

interface Ticket {
    id: string;
    ticket_number: string;
    user_id: string;
    branch_id: string | null;
    category: string;
    priority: string;
    subject: string;
    description: string;
    status: string;
    assigned_to: string | null;
    created_at: string;
    updated_at: string;
    user?: {
        name: string;
        email: string;
    };
    branch?: {
        name: string;
    };
}

interface UserStats {
    role: string;
    count: number;
}

const TechSupportDashboard: React.FC = () => {
    const { user } = useAuth();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [stats, setStats] = useState({
        total: 0,
        open: 0,
        inProgress: 0,
        resolved: 0
    });
    const [userStats, setUserStats] = useState<UserStats[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            // Fetch tickets with user info
            const { data: ticketsData, error: ticketsError } = await supabase
                .from('support_tickets')
                .select(`
          *,
          user:profiles!support_tickets_user_id_fkey(name, email),
          branch:branches(name)
        `)
                .order('created_at', { ascending: false });

            if (ticketsError) throw ticketsError;

            const ticketsList = ticketsData || [];
            setTickets(ticketsList);

            // Calculate stats
            setStats({
                total: ticketsList.length,
                open: ticketsList.filter(t => t.status === 'acik').length,
                inProgress: ticketsList.filter(t => t.status === 'islemde').length,
                resolved: ticketsList.filter(t => t.status === 'cozuldu' || t.status === 'kapatildi').length
            });

            // Fetch user stats
            const { data: profilesData } = await supabase
                .from('profiles')
                .select('role, unit_type')
                .eq('is_active', true);

            if (profilesData) {
                const roleMap: Record<string, number> = {};
                profilesData.forEach(p => {
                    const key = p.unit_type || p.role || 'Diğer';
                    roleMap[key] = (roleMap[key] || 0) + 1;
                });
                const userStatsArray = Object.entries(roleMap).map(([role, count]) => ({ role, count }));
                setUserStats(userStatsArray);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getRoleName = (role: string): string => {
        const names: Record<string, string> = {
            admin: 'Admin',
            branch: 'Şube Yöneticisi',
            unit: 'Birim',
            sales: 'Satış',
            education_consultant: 'İtalya Ekibi',
            accounting: 'Muhasebe',
            teknik_destek: 'Teknik Destek',
            testprep: 'TestPrep',
            visa_consultant: 'Vize',
            customer_service: 'Müşteri Hizmetleri'
        };
        return names[role] || role;
    };

    const getRoleColor = (role: string): string => {
        const colors: Record<string, string> = {
            admin: 'bg-red-500',
            sales: 'bg-blue-500',
            education_consultant: 'bg-green-500',
            accounting: 'bg-purple-500',
            teknik_destek: 'bg-cyan-500',
            testprep: 'bg-yellow-500',
            visa_consultant: 'bg-orange-500'
        };
        return colors[role] || 'bg-gray-500';
    };

    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            acik: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
            islemde: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
            beklemede: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
            cozuldu: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            kapatildi: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
        };
        const labels: Record<string, string> = {
            acik: 'Açık',
            islemde: 'İşlemde',
            beklemede: 'Beklemede',
            cozuldu: 'Çözüldü',
            kapatildi: 'Kapatıldı'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.acik}`}>
                {labels[status] || status}
            </span>
        );
    };

    const getPriorityBadge = (priority: string) => {
        const styles: Record<string, string> = {
            dusuk: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
            orta: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
            yuksek: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
            acil: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
        };
        const labels: Record<string, string> = {
            dusuk: 'Düşük',
            orta: 'Orta',
            yuksek: 'Yüksek',
            acil: 'Acil'
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[priority] || styles.orta}`}>
                {labels[priority] || priority}
            </span>
        );
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = searchQuery === '' ||
            ticket.ticket_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ticket.user?.name?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const urgentCount = tickets.filter(t => t.priority === 'acil' && t.status === 'acik').length;
    const highPriorityCount = tickets.filter(t => t.priority === 'yuksek' && t.status === 'acik').length;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        Teknik Destek Paneli
                        <span className="text-xs font-normal text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                            v2.2
                        </span>
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Hoş geldin, {user?.name?.split(' ')[0] || 'Teknik Destek'}
                    </p>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 transition-colors">
                    <Plus className="w-4 h-4" />
                    Yeni Talep
                </button>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Left Column - Stats and Tickets */}
                <div className="col-span-12 lg:col-span-8 space-y-6">
                    {/* Urgency Analysis */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4" />
                            Aciliyet Analizi
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Acil Durumlar</p>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                        <div
                                            className="bg-red-500 h-2 rounded-full"
                                            style={{ width: `${(urgentCount / Math.max(stats.open, 1)) * 100}%` }}
                                        />
                                    </div>
                                    <span className="text-sm font-semibold text-red-600">{urgentCount} Talep</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Yüksek Öncelik</p>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                        <div
                                            className="bg-orange-500 h-2 rounded-full"
                                            style={{ width: `${(highPriorityCount / Math.max(stats.open, 1)) * 100}%` }}
                                        />
                                    </div>
                                    <span className="text-sm font-semibold text-orange-600">{highPriorityCount} Talep</span>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Yeni Açılanlar</p>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                        <div
                                            className="bg-blue-500 h-2 rounded-full"
                                            style={{ width: `${(stats.open / Math.max(stats.total, 1)) * 100}%` }}
                                        />
                                    </div>
                                    <span className="text-sm font-semibold text-blue-600">{stats.open} Talep</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-4 gap-4">
                        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                                    <MessageSquare className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Tümü</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                                    <p className="text-xs text-gray-500">Toplam Talep</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">İşlem Bekleyen</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.open}</p>
                                    <p className="text-xs text-gray-500">Açık Talepler</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Çözüm Sürecinde</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.inProgress}</p>
                                    <p className="text-xs text-gray-500">İşlemde</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Tamamlanan</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.resolved}</p>
                                    <p className="text-xs text-gray-500">Çözüldü</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tickets List */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                            <div className="flex items-center justify-between">
                                <h3 className="font-semibold text-gray-900 dark:text-white">Aktif Talepler</h3>
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Talep ara..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none w-48"
                                        />
                                    </div>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                                    >
                                        <option value="all">Tarihe Göre</option>
                                        <option value="acik">Açık</option>
                                        <option value="islemde">İşlemde</option>
                                        <option value="beklemede">Beklemede</option>
                                        <option value="cozuldu">Çözüldü</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="divide-y divide-gray-200 dark:divide-gray-800">
                            {filteredTickets.length === 0 ? (
                                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                    <p>Henüz destek talebi bulunmuyor</p>
                                </div>
                            ) : (
                                filteredTickets.slice(0, 10).map(ticket => (
                                    <div
                                        key={ticket.id}
                                        onClick={() => setSelectedTicket(ticket)}
                                        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3">
                                                <div className={`w-2 h-2 rounded-full mt-2 ${ticket.priority === 'acil' ? 'bg-red-500' :
                                                    ticket.priority === 'yuksek' ? 'bg-orange-500' :
                                                        'bg-blue-500'
                                                    }`} />
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                                                            {ticket.ticket_number}
                                                        </span>
                                                        {getPriorityBadge(ticket.priority)}
                                                    </div>
                                                    <h4 className="font-medium text-gray-900 dark:text-white mt-1">
                                                        {ticket.subject}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                                                        <span className="flex items-center gap-1">
                                                            <Users className="w-3 h-3" />
                                                            {ticket.user?.name || 'Bilinmeyen'}
                                                        </span>
                                                        <span>•</span>
                                                        <span>{ticket.category === 'hata_bildirimi' ? 'Hata Bildirimi' :
                                                            ticket.category === 'soru' ? 'Soru' :
                                                                ticket.category === 'ozellik_talebi' ? 'Özellik Talebi' : 'Diğer'}</span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 text-right">
                                                <div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(ticket.created_at)}</p>
                                                    <div className="mt-1">{getStatusBadge(ticket.status)}</div>
                                                </div>
                                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - AI Suggestions and User Stats */}
                <div className="col-span-12 lg:col-span-4 space-y-6">
                    {/* AI Suggestions */}
                    {urgentCount > 0 && (
                        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 p-5">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-yellow-600" />
                                AI Önerileri
                            </h3>
                            <div className="bg-white/80 dark:bg-gray-800/80 rounded-lg p-3 border border-yellow-200 dark:border-yellow-700">
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    <span className="text-red-600 font-medium">{urgentCount} Acil</span> talep beklemede.
                                    Önce bunlara öncelik verin.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* User Stats */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Kullanıcı İstatistikleri
                        </h3>
                        <div className="space-y-2">
                            {userStats.map((stat, index) => (
                                <div key={index} className="flex items-center justify-between py-1">
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${getRoleColor(stat.role)}`} />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">{getRoleName(stat.role)}</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{stat.count}</span>
                                </div>
                            ))}
                            <div className="flex items-center justify-between py-1 border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Toplam Kullanıcı</span>
                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                    {userStats.reduce((sum, s) => sum + s.count, 0)}
                                </span>
                            </div>
                        </div>
                        <button className="w-full mt-4 py-2 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/30 dark:hover:bg-orange-900/50 text-orange-700 dark:text-orange-400 rounded-lg text-sm font-medium transition-colors">
                            Kullanıcıları Yönet
                        </button>
                    </div>
                </div>
            </div>
            {/* Ticket Detail Modal */}
            {selectedTicket && (
                <TicketDetailModal
                    isOpen={!!selectedTicket}
                    onClose={() => setSelectedTicket(null)}
                    ticket={selectedTicket}
                    onUpdate={fetchData}
                />
            )}
        </div>
    );
};

export default TechSupportDashboard;
